/**
 * Why a scheduled appointment was removed. The office tracks every removal,
 * so each removing gesture asks for one of these codes; "other" requires a
 * free-text note. Mirrors DispatchAppointment::REMOVAL_REASONS in the
 * backend — keep both lists in sync.
 */
export const APPOINTMENT_REMOVAL_REASON_OTHER = "other";

export const APPOINTMENT_REMOVAL_REASONS: { value: string; label: string }[] = [
  { value: "customer_cancelled", label: "Customer cancelled" },
  { value: "customer_reschedule", label: "Customer asked to reschedule" },
  { value: "customer_unreachable", label: "Could not reach customer" },
  { value: "tech_unavailable", label: "Technician unavailable" },
  { value: "parts_pending", label: "Waiting on parts" },
  { value: "duplicate_job", label: "Duplicate job" },
  { value: "dispatcher_error", label: "Scheduled by mistake" },
  { value: APPOINTMENT_REMOVAL_REASON_OTHER, label: "Other" },
];

/** Labels for every code the backend can store, including system-only ones. */
export const APPOINTMENT_REMOVAL_REASON_LABELS: Record<string, string> = {
  ...Object.fromEntries(APPOINTMENT_REMOVAL_REASONS.map((r) => [r.value, r.label])),
  automation: "Automation",
  orphan_repair: "Orphaned slot repair",
  system: "System",
};

export const APPOINTMENT_REMOVAL_NOTE_MAX_LENGTH = 1000;

/** Request fields sent with any appointment-removing call. */
export interface AppointmentRemovalPayload {
  removal_reason?: string;
  removal_note?: string;
}

export function removalReasonLabel(code?: string | null): string | null {
  if (!code) return null;
  return APPOINTMENT_REMOVAL_REASON_LABELS[code] ?? code;
}

/** True when the payload satisfies the backend rule: a code, plus a note for "other". */
export function isRemovalPayloadComplete(payload: AppointmentRemovalPayload | null | undefined): boolean {
  const reason = payload?.removal_reason?.trim();
  if (!reason) return false;
  if (reason === APPOINTMENT_REMOVAL_REASON_OTHER && !payload?.removal_note?.trim()) return false;
  return true;
}
